// ============================================================================
// Spinner Component
// Feature: 099-app-review-optimization
// Created: 2026-01-11
// Description: Loading spinner component
// ============================================================================

import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { clsx } from 'clsx';

/**
 * Spinner sizes
 */
export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Spinner variants
 */
export type SpinnerVariant = 'default' | 'primary' | 'white';

/**
 * Spinner props
 */
export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  label?: string;
}

/**
 * Size styles
 */
const sizeStyles: Record<SpinnerSize, string> = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12',
};

/**
 * Variant styles
 */
const variantStyles: Record<SpinnerVariant, string> = {
  default: 'text-[var(--color-text-secondary)]',
  primary: 'text-[var(--color-accent)]',
  white: 'text-white',
};

/**
 * Spinner Component
 */
export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = 'md', variant = 'default', label, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="status"
        aria-label={label || 'Loading'}
        className={clsx('inline-flex items-center gap-2', className)}
        {...props}
      >
        <svg
          className={clsx('animate-spin', sizeStyles[size], variantStyles[variant])}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        {label && (
          <span className="text-sm text-[var(--color-text-secondary)]">{label}</span>
        )}
        <span className="sr-only">{label || 'Loading...'}</span>
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';

/**
 * Full page loading spinner
 */
export interface LoadingOverlayProps extends SpinnerProps {
  isVisible?: boolean;
  message?: string;
}

export const LoadingOverlay = forwardRef<HTMLDivElement, LoadingOverlayProps>(
  ({ isVisible = true, message, size = 'lg', ...props }, ref) => {
    if (!isVisible) return null;

    return (
      <div
        ref={ref}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/15 dark:bg-black/45 backdrop-blur-sm"
      >
        <div className="flex flex-col items-center gap-4">
          <Spinner size={size} variant="primary" {...props} />
          {message && (
            <p className="text-sm text-[var(--color-text-secondary)]">{message}</p>
          )}
        </div>
      </div>
    );
  }
);

LoadingOverlay.displayName = 'LoadingOverlay';

/**
 * Inline loading indicator
 */
export interface InlineLoadingProps extends SpinnerProps {
  text?: string;
}

export const InlineLoading = forwardRef<HTMLDivElement, InlineLoadingProps>(
  ({ text = 'Loading...', size = 'sm', ...props }, ref) => {
    return (
      <div ref={ref} className="inline-flex items-center gap-2">
        <Spinner size={size} variant="primary" {...props} />
        <span className="text-sm text-[var(--color-text-secondary)]">{text}</span>
      </div>
    );
  }
);

InlineLoading.displayName = 'InlineLoading';

export default Spinner;
